<script setup lang="ts">

import {ArrowRight, ChevronDown, Loader2Icon} from "lucide-vue-next";
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'
import {onMounted, type Ref, ref, useTemplateRef, watch} from "vue";
import {label} from "../../mixins/translations.js";
import {useEnrollmentStore} from "../../stores/enrollment.ts";
import ContactDetails from "./Components/ContactDetails.vue";
import Student from "./Components/Student.vue";

const store = useEnrollmentStore();

const currentIndex: Ref<string> = ref('');
const removeStudent = () => {
    store.removeStudent(currentIndex.value);
    currentIndex.value = store.students[0].uuid.toString();
}
const studentErrors = ref([]);
const contactDetailsErrors = ref({});
const studentRefs = ref([]);
const contactDetailsRef = useTemplateRef('contact-details')
const save = () => {
    store.saveStep1(true).then(() => {
        store.openStep1 = false;
    }).catch((errors) => {
        //Errors not caught by yup
        studentErrors.value = store.filterLaravelErrors(errors, 'students');
        contactDetailsErrors.value = store.filterLaravelErrors(errors, 'contact_details');
    });
}

const addStudent = () => {
    store.addStudent().then((uuid) => {
        currentIndex.value = String(uuid);
    })
}

const validateAll = () => {
    if (Object.keys(store.contactDetails).length !== 0) {
        contactDetailsRef.value.validate()
    }
    studentRefs.value.every(student => student.validate())
}

//Reset steps
watch(store.students, () => {
    store.currentStep = store.EnrollmentStatus.step_1;
})

watch(store.contactDetails, () => {
    store.currentStep = store.EnrollmentStatus.step_1;
})

onMounted(() => {
    currentIndex.value = store.students[0].uuid;
})

const toggleStep = () => {
    if(store.currentStep > 1) {
        store.openStep1 = !store.openStep1;
    }
}

</script>

<template>
    <!-- STEP 1 -->
    <div class="rounded-md overflow-hidden">
        <div class="flex w-full items-center justify-between bg-white py-4 hidden md:flex">
            <span  class="font-bold  text-lg text-black">{{ label('site.enrollment.step_1')}}: {{ label('site.enrollment.step_1.header')}}</span>
            <ChevronDown :class="{
                'rotate-180': store.openStep1
            }" class="h-5 w-5 text-primary cursor-pointer" @click="toggleStep" />
        </div>
        <span class="font-bold md:hidden  text-lg text-black" v-if="store.openStep1">{{ label('site.enrollment.step_1')}}: {{ label('site.enrollment.step_1.header')}}</span>

        <div class="bg-gray-100 px-6 pt-2 pb-6" v-show="store.openStep1">
            <p class="py-3 text-gray-800 text-base leading-relaxed">
                {{ label('site.enrollment.step_1.description')}}
            </p>

            <ContactDetails ref="contact-details" v-model="store.contactDetails" :errors="contactDetailsErrors"></ContactDetails>

            <div class="py-3">
                <TabsRoot v-model="currentIndex" default-value="0" >
                    <div class="border-b border-gray-300">
                        <div class="flex items-end gap-4 lg:gap-12  -mb-px">
                            <p class="mb-2 text-base leading-relaxed text-gray-900" v-text="label('site.enrollment.student_data')"></p>

                            <TabsList class="flex gap-2 bg-transparent">
                                <TabsTrigger
                                    v-for="(student, index) in store.students"
                                    :key="student.uuid"
                                    :value="String(student.uuid)"
                                    class="px-6 py-2 font-semibold rounded-t-md border-t border-l border-r transition-colors
           data-[state=active]:bg-gray-100 data-[state=active]:text-gray-900 data-[state=active]:border-primary
           data-[state=inactive]:bg-gray-200 data-[state=inactive]:text-gray-700 data-[state=inactive]:border-gray-300"
                                >
                                    {{ currentIndex === String(student.uuid) ? label('site.student') : '' }} {{ index + 1 }}
                                </TabsTrigger>
                            </TabsList>
                        </div>
                    </div>

                    <TabsContent
                        v-for="(student, index) in store.students"
                        :key="student.uuid"
                        :value="String(student.uuid)"
                        class="mt-4"
                    >
                        <Student v-model="store.students[index]" :errors="studentErrors[index]" :index="index"  :ref="el => { if (el) studentRefs[index] = el }"/>
                    </TabsContent>
                </TabsRoot>
            </div>

            <p class="mt-4 italic text-sm text-gray-700" v-text="label('site.enrollment.step_1.description_bottom')">
            </p>

            <div class="mt-8 space-y-3">
                <div class="text-right" v-if="!store.isValidStep1">
                    <span class="text-sm text-primary" v-text="label('site.enrollment.not_all_filled')">
                    </span>
                </div>

                <div class="flex flex-wrap items-center justify-between gap-4">
                    <button v-if="store.studentCount > 1" @click="removeStudent" type="button" class="inline-flex items-center gap-2 text-primary font-semibold">
                        <ArrowRight class="h-4 w-4" />
                        {{ label('site.enrollment.remove_student')}}
                    </button>

                    <div class="flex flex-wrap gap-3 ml-auto">
                        <button type="button" @click="store.isValidStep1 ? addStudent(): validateAll()" class="rounded-md border border-primary px-6 py-2 font-semibold text-primary bg-white hover:bg-gray-50">
                            <Loader2Icon v-if="store.isLoading" class="w-5 h-5 animate-spin " />
                            <span v-else v-text="label('site.enrollment.save_and_add')"></span>
                        </button>
                        <button @click.prevent="store.isValidStep1 ? save(): validateAll()" type="button" class="rounded-md bg-primary px-6 py-2 font-semibold text-white hover:bg-primary-400" >
                            <Loader2Icon v-if="store.isLoading" class="w-5 h-5 animate-spin " />
                            <span v-else v-text="label('site.enrollment.step_1.button')"></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
